/**
 * Active alerts, found by rules over the demo engine rather than written by
 * hand — so "checkout conversion down 14%" is true of the funnel numbers the
 * same screen shows. Alerts describe *now* (the last couple of days against the
 * week before), so the date range filter does not move them; an entity filter
 * narrows them to that entity.
 */

import { SEVERITIES, type Alert, type Severity } from '@/api/types'
import { formatCurrency, formatPercent, formatRatio } from '@/design/format'
import { utcLabel } from '@/features/integrations/websites/utcLabel'
import { addDays, DEFAULT_FILTERS, type GlobalFilters } from '@/lib/filters'

import { scopeFor, type DemoDataset } from './engine'

const CHECKOUT_DROP_THRESHOLD = -0.08
const PAYMENT_FAILURE_RISE = 1.4
const ROAS_FLOOR = 1.2

function rate(numerator: number, denominator: number): number {
  return denominator > 0 ? numerator / denominator : 0
}

/**
 * The one demo website whose connector reports a failed reconcile, so the
 * Overview strip shows a connector alert (wave 9). `MockApiClient.websiteHealth`
 * reads the same figures, so that website's Integrations card says "Needs
 * attention" for the same reason the alert gives.
 */
export const DEMO_CONNECTOR_FAILURE = {
  websiteId: 'web_676_prod',
  sent: 41,
  skipped: 2,
  failed: 3,
  errorClass: 'http_5xx',
  /** The reconcile window: the hour before it finished, which was this long ago. */
  finishedMinutesAgo: 7,
} as const

/**
 * The demo connector's failed-reconcile alert, worded and timed as `GET /overview/alerts`
 * does it: `occurredAt` is when the report arrived, the end of its run.
 */
export function demoConnectorAlert(websiteName: string, environment: string, now: Date): Alert {
  const { websiteId, sent, skipped, failed, errorClass, finishedMinutesAgo } = DEMO_CONNECTOR_FAILURE
  const to = new Date(now.getTime() - finishedMinutesAgo * 60_000)
  const from = new Date(to.getTime() - 60 * 60_000)
  return {
    id: `connector_reconcile_failed_${websiteId}`,
    title: 'Connector reconciliation failed',
    severity: 'high',
    category: 'technical',
    entity: null,
    subject: `${websiteName} (${environment})`,
    explanation: `Items failed with ${errorClass}; the last run reconciled ${utcLabel(from)} to ${utcLabel(to)}: ${sent} sent, ${skipped} skipped, ${failed} failed.`,
    drillDown: { path: '/integrations' },
    occurredAt: to.toISOString(),
    facts: [],
  }
}

export function buildAlerts(dataset: DemoDataset, filters: GlobalFilters): Alert[] {
  const { tenant, today, now } = dataset
  const money = { currency: tenant.currency, locale: tenant.locale }
  const scope = scopeFor(tenant, { ...DEFAULT_FILTERS, environments: filters.environments })
  if (!scope.includesProduction) return []

  const recentDays = tenant.anomalies.recentDays
  const recentWindow = { from: addDays(today, -(recentDays - 1)), to: today, days: recentDays }
  const baselineWindow = { from: addDays(recentWindow.from, -7), to: addDays(recentWindow.from, -1), days: 7 }
  const recent = dataset.period(recentWindow, scope)
  const baseline = dataset.period(baselineWindow, scope)

  // A rule's alert is timed by its rank below; one that knows its own moment keeps it.
  const alerts: Array<Omit<Alert, 'occurredAt'> & { occurredAt?: string }> = []

  const checkoutNow = rate(recent.stages.payment_attempted, recent.stages.checkout_started)
  const checkoutBefore = rate(baseline.stages.payment_attempted, baseline.stages.checkout_started)
  const checkoutChange = checkoutBefore > 0 ? checkoutNow / checkoutBefore - 1 : 0
  if (checkoutChange <= CHECKOUT_DROP_THRESHOLD) {
    alerts.push({
      id: 'alert_checkout_conversion',
      title: `Checkout conversion down ${formatPercent(Math.abs(checkoutChange), 0)}`,
      severity: checkoutChange <= -0.12 ? 'high' : 'medium',
      category: 'conversion',
      entity: null,
      subject: 'Website funnel · Checkout',
      explanation: `${formatPercent(checkoutNow)} of started checkouts reached payment in the last ${recentDays} days, against ${formatPercent(checkoutBefore)} over the 7 days before.`,
      drillDown: { path: '/analytics/funnel', params: { stage: 'checkout_started' } },
      facts: [
        {
          key: 'funnel_step_rate:funnel_step:payment_attempted',
          kind: 'funnel_step_rate',
          entityName: 'Payment attempted',
          value: checkoutNow.toFixed(4),
          unit: 'fraction',
          currency: null,
          period: { start: recentWindow.from, end: recentWindow.to },
          comparison: {
            basis: 'previous_period',
            value: checkoutBefore.toFixed(4),
            period: { start: baselineWindow.from, end: baselineWindow.to },
          },
          direction: 'down',
          confidence: 'medium',
        },
      ],
    })
  }

  const failureNow = 1 - rate(recent.stages.purchase_completed, recent.stages.payment_attempted)
  const failureBefore = 1 - rate(baseline.stages.purchase_completed, baseline.stages.payment_attempted)
  if (failureNow > 0.05 && failureNow >= failureBefore * PAYMENT_FAILURE_RISE) {
    alerts.push({
      id: 'alert_payment_failures',
      title: 'Payment failures elevated',
      severity: failureNow >= 0.1 ? 'critical' : 'high',
      category: 'payment',
      entity: null,
      subject: 'Payments',
      explanation: `${formatPercent(failureNow)} of payment attempts failed in the last ${recentDays} days, against ${formatPercent(failureBefore)} over the 7 days before.`,
      drillDown: { path: '/analytics/abandonment', params: { stage: 'payment_attempted' } },
      facts: [],
    })
  }

  for (const competition of dataset.onSaleOn(today)) {
    const status = dataset.competitionStatus(competition)
    if (status.pacing !== 'behind') continue
    const entity = { type: 'competition' as const, id: competition.id, label: competition.name }
    alerts.push({
      id: `alert_pacing_${competition.id}`,
      title: `${competition.name} pacing below expected`,
      severity: status.daysLeft <= 5 ? 'high' : 'medium',
      category: 'competition',
      entity,
      subject: competition.name,
      explanation: `${formatPercent(status.sellThrough, 0)} of entries sold with ${formatPercent(status.expected, 0)} of the run elapsed; closes in ${status.daysLeft} ${status.daysLeft === 1 ? 'day' : 'days'}.`,
      drillDown: { path: `/analytics/competitions/${competition.id}`, entity },
      facts: [
        {
          key: `competition_sell_through:competition:${competition.id}`,
          kind: 'competition_sell_through',
          entityName: competition.name,
          value: status.sellThrough.toFixed(4),
          unit: 'fraction',
          currency: null,
          period: { start: today, end: today },
          comparison: { basis: 'straight_line_target', value: status.expected.toFixed(4), period: null },
          direction: 'down',
          confidence: 'high',
        },
      ],
    })
  }

  const lastWeek = dataset.period({ from: addDays(today, -6), to: today, days: 7 }, scope)
  for (const channel of tenant.channels) {
    const totals = lastWeek.channels.get(channel.id)
    if (!totals || totals.spend <= 0) continue
    const roas = totals.attributedRevenue / totals.spend
    if (roas >= ROAS_FLOOR) continue
    const entity = { type: 'channel' as const, id: channel.id, label: channel.name }
    alerts.push({
      id: `alert_roas_${channel.id}`,
      title: roas < 1 ? `${channel.name} returning less than it costs` : `${channel.name} ROAS below target`,
      severity: roas < 1 ? 'medium' : 'low',
      category: 'advertising',
      entity,
      subject: channel.name,
      explanation: `ROAS of ${formatRatio(roas)} on ${formatCurrency(totals.spend, money)} spend over the last 7 days.`,
      drillDown: { path: '/marketing/channels', entity },
      facts: [],
    })
  }

  // Production only, as the API's default: `scope.includesProduction` held to get here.
  const failingSite = tenant.websites.find((website) => website.id === DEMO_CONNECTOR_FAILURE.websiteId)
  if (failingSite) alerts.push(demoConnectorAlert(failingSite.name, failingSite.environment, now))

  const rank = (severity: Severity) => SEVERITIES.indexOf(severity)
  return alerts
    .filter((alert) => {
      const { competition, channel } = filters.dimensions
      if (competition && alert.entity?.type === 'competition') return alert.entity.id === competition
      if (channel && alert.entity?.type === 'channel') return alert.entity.id === channel
      return true
    })
    .sort((a, b) => rank(a.severity) - rank(b.severity) || a.id.localeCompare(b.id))
    .map((alert, index) => ({
      ...alert,
      occurredAt: alert.occurredAt ?? new Date(now.getTime() - (index * 37 + 12) * 60_000).toISOString(),
    }))
}
